import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { Button, Flex, Stack, Text } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	DATE_FORMATS,
	formatStringDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { METHOD_OPTIONS } from '../MethodSelectStep.types';
import { Body, HeaderPanel, SessionList } from './styles';
import type { SignatureSuccessDetails } from './SignatureSuccessConfirmation.types';
import { DetailBlock } from '@dealstack/frontend/shared/components/EidEasySigningModal/steps/SignatureSuccessConfirmation/components/DetailBlock';

export const SignatureSuccessConfirmation = ({
	signerName,
	signedAt,
	signingMethod,
	session,
	showSessionDetails = true,
	onDownloadSignedPdf,
	isDownloading,
}: SignatureSuccessDetails) => {
	const tCommonUi = useTranslationCommonUi();
	const theme = useTheme();

	const methodLabelKey = METHOD_OPTIONS.find(
		(option) => option.method === signingMethod
	)?.labelKey;
	const methodLabel = methodLabelKey ? tCommonUi(methodLabelKey) : null;
	const signedAtLabel = formatStringDateToUiWithFallback(
		signedAt ?? undefined,
		DATE_FORMATS.DATE_TIME_ALT
	);

	const sessionRows = useMemo(
		() =>
			[
				{
					key: tCommonUi('eid_easy_signing.success.started_at'),
					value: signedAtLabel,
				},
				{
					key: tCommonUi('eid_easy_signing.success.ip_address'),
					value: session?.ipAddress,
				},
				{
					key: tCommonUi('eid_easy_signing.success.location'),
					value: session?.location,
				},
				{
					key: tCommonUi('eid_easy_signing.success.browser'),
					value: session?.browser,
				},
			].filter((row): row is { key: string; value: string } => !!row.value),
		[tCommonUi, signedAtLabel, session]
	);

	return (
		<Stack gap={0} w="100%" data-testid={TEST_ELEMENT_IDS.SIGNATURE_SUCCESS_CONFIRMATION}>
			<HeaderPanel>
				<Icon variant="c-check" size="xl" color={theme.v3.colors.dark[4]} />
				<Text fw={600} size="lg">
					{tCommonUi('eid_easy_signing.success.title')}
				</Text>
			</HeaderPanel>
			<Body gap="md">
				<DetailBlock
					label={tCommonUi('eid_easy_signing.success.signer')}
					value={signerName}
				/>
				<DetailBlock
					label={tCommonUi('eid_easy_signing.success.method')}
					value={methodLabel}
				/>

				{showSessionDetails && sessionRows.length > 0 && (
					<Stack gap="xs">
						<Flex align="center" gap="xs" c="dimmed">
							<Icon variant="monitor-02" size="lg" color={theme.v3.colors.dark[4]} />
							<Text size="sm" c={theme.v3.colors.dark[2]}>
								{tCommonUi('eid_easy_signing.success.session_details', {
									defaultValue: 'Session details',
								})}
							</Text>
						</Flex>
						<SessionList>
							{sessionRows.map(({ key, value }) => (
								<li key={key}>
									{key}: {value}
								</li>
							))}
						</SessionList>
					</Stack>
				)}

				{onDownloadSignedPdf && (
					<Button
						variant="default"
						fullWidth
						mt="sm"
						loading={isDownloading}
						leftSection={<Icon variant="download" size="md" />}
						onClick={onDownloadSignedPdf}
						data-testid={TEST_ELEMENT_IDS.SIGNATURE_SUCCESS_DOWNLOAD_BUTTON}
					>
						{tCommonUi('eid_easy_signing.success.download_signed_pdf')}
					</Button>
				)}
			</Body>
		</Stack>
	);
};
